import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  Skeleton,
  Text,
} from '@musa.tech/design-system';
import { useBucketRequests } from '@/hooks/useAgreementsOnDemand';
import type { BucketMovement, BucketRequest } from '@/integrations/agreements/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskGeneratorName } from '@/utils/demoMode';
import { PERIOD_LABELS, establishmentName, formatAddress, formatDay, haulerName } from '@/utils/onDemandRequestRules';
import { BUCKET_LOCATION_LABELS, BUCKET_STATUS_DISPLAY, BUCKET_TYPE_LABELS, describeBucketNumbers } from '@/utils/bucketRequestRules';
import {
  countRequestStatuses,
  describeRequestDates,
  describeRequestLocation,
  describeRequestOperations,
  describeRequestPeriod,
  requestBucketNumbers,
} from './bucketRequestSummary';
import { OnDemandFilterBar } from './OnDemandFilterBar';
import {
  EMPTY_ON_DEMAND_LIST_FILTERS,
  hasAppliedFilters,
  toMovementsFilter,
  type OnDemandListFilters,
} from './onDemandListFilters';

export const BUCKET_MOVEMENTS_PAGE_SIZE = 25;

/**
 * Movimentações de equipamento do gerador — as pedidas pelo portal e as agendadas pela Musa no
 * Hub para os contratos dele. Sem ação: aceitar, recusar e registrar são do transportador;
 * cancelar é da Musa.
 *
 * Uma linha por solicitação: "4 retiradas" pedidas num envio são uma linha, que abre as quatro
 * operações — o transportador responde e registra cada uma, e é aí que o status pode divergir.
 */
export const MinhasCacambasList: React.FC = () => {
  const [filters, setFilters] = useState<OnDemandListFilters>(EMPTY_ON_DEMAND_LIST_FILTERS);
  const [page, setPage] = useState(1);
  const { isDemoMode } = useDemoMode();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const { data, isLoading, isError } = useBucketRequests({
    ...toMovementsFilter(filters),
    page,
    pageSize: BUCKET_MOVEMENTS_PAGE_SIZE,
  });
  const items = data ?? [];
  // a listagem do serviço não devolve total: página cheia é sinal de que pode haver mais
  const hasNext = items.length === BUCKET_MOVEMENTS_PAGE_SIZE;
  const filtered = hasAppliedFilters(filters);

  return (
    <div className="flex flex-col gap-4">
      <OnDemandFilterBar
        service="bucket"
        filters={filters}
        onChange={(next) => {
          setFilters(next);
          setPage(1);
        }}
      />

      {isLoading ? (
        <div className="flex flex-col gap-2" data-testid="bucket-movements-loading">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertTitle>Não foi possível carregar as movimentações de equipamento</AlertTitle>
          <AlertDescription>
            O serviço de coletas está indisponível no momento. Tente novamente em alguns minutos.
          </AlertDescription>
        </Alert>
      ) : !items.length ? (
        <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-neutral-200 px-6 py-10 text-center">
          <Text variant="label" size="medium" className="text-neutral-900">
            {filtered ? 'Nenhuma movimentação de equipamento com esses filtros' : 'Nenhuma movimentação de equipamento ainda'}
          </Text>
          <Text variant="body" size="small" className="max-w-md text-neutral-500">
            {filtered
              ? 'Troque ou limpe os filtros para ver as outras movimentações.'
              : 'Os pedidos de colocação, retirada e troca aparecem aqui, com o status da resposta do transportador.'}
          </Text>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-white">
            <DataTable>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead className="w-10"><span className="sr-only">Abrir operações</span></DataTableHead>
                  <DataTableHead>Data</DataTableHead>
                  <DataTableHead>Operação</DataTableHead>
                  <DataTableHead>Estabelecimento</DataTableHead>
                  <DataTableHead>Resíduo</DataTableHead>
                  <DataTableHead>Equipamento</DataTableHead>
                  <DataTableHead>Transportador</DataTableHead>
                  <DataTableHead>Status</DataTableHead>
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {items.map((request) => (
                  <BucketRequestRows
                    key={request.id}
                    request={request}
                    expanded={expanded.has(request.id)}
                    onToggle={() => toggle(request.id)}
                    isDemoMode={isDemoMode}
                  />
                ))}
              </DataTableBody>
            </DataTable>
          </div>

          {(page > 1 || hasNext) && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                Anterior
              </Button>
              <Text variant="body" size="small" className="text-neutral-500">
                Página {page}
              </Text>
              <Button variant="outline" size="sm" disabled={!hasNext} onClick={() => setPage(page + 1)}>
                Próxima
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface BucketRequestRowsProps {
  request: BucketRequest;
  expanded: boolean;
  onToggle: () => void;
  isDemoMode: boolean;
}

/** A linha da solicitação e, aberta, uma linha por operação dela. */
const BucketRequestRows: React.FC<BucketRequestRowsProps> = ({ request, expanded, onToggle, isDemoMode }) => {
  const { movements } = request;
  const [first] = movements;
  if (!first) return null;

  const several = movements.length > 1;
  const location = describeRequestLocation(movements);
  const numbers = requestBucketNumbers(movements);
  const statuses = countRequestStatuses(movements);

  return (
    <>
      <DataTableRow data-testid={`bucket-request-row-${request.id}`}>
        <DataTableCell>
          {several && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              aria-expanded={expanded}
              aria-label={expanded ? 'Fechar operações da solicitação' : 'Abrir operações da solicitação'}
              onClick={onToggle}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          )}
        </DataTableCell>
        <DataTableCell>
          <Text variant="body" size="small" className="text-neutral-900">
            {describeRequestDates(movements)}
          </Text>
          <Text variant="body" size="small" className="text-neutral-500">
            {describeRequestPeriod(movements)}
          </Text>
        </DataTableCell>
        <DataTableCell>
          <Text variant="body" size="small" className="text-neutral-900">
            {several ? describeRequestOperations(movements) : BUCKET_TYPE_LABELS[first.type]}
          </Text>
          {location && (
            <Text variant="body" size="small" className="text-neutral-500">
              {location}
            </Text>
          )}
        </DataTableCell>
        <DataTableCell>
          <Text variant="body" size="small" className="text-neutral-900">
            {maskGeneratorName(establishmentName(first.generator), isDemoMode)}
          </Text>
          <Text variant="body" size="small" className="text-neutral-500">
            {formatAddress(first.address)}
          </Text>
        </DataTableCell>
        {/* o resíduo é do contrato: todas as operações da solicitação levam o mesmo */}
        <DataTableCell>
          <Text variant="body" size="small" className="text-neutral-900">
            {first.material.name ?? '—'}
          </Text>
        </DataTableCell>
        <DataTableCell>
          <Text variant="body" size="small" className="text-neutral-900">
            {first.packaging.name ?? '—'}
          </Text>
          <Text variant="body" size="small" className="text-neutral-500">
            {several
              ? numbers.length ? `Nº ${numbers.join(', ')}` : 'Número definido pelo transportador'
              : describeBucketNumbers(first)}
          </Text>
        </DataTableCell>
        <DataTableCell>{haulerName(first.hauler)}</DataTableCell>
        <DataTableCell>
          <div className="flex flex-wrap gap-1">
            {statuses.map(({ status, count }) => {
              const display = BUCKET_STATUS_DISPLAY[status];
              // todas no mesmo status: o selo diz só qual; divergindo, quantas em cada um
              const label = display?.label ?? '—';

              return (
                <Badge key={status} variant={display?.variant ?? 'secondary'} className="whitespace-nowrap">
                  {statuses.length > 1 ? `${count} ${label}` : label}
                </Badge>
              );
            })}
          </div>
        </DataTableCell>
      </DataTableRow>

      {several && expanded && movements.map((movement) => (
        <BucketMovementRow key={movement.id} movement={movement} />
      ))}
    </>
  );
};

/** Uma operação dentro da solicitação aberta: o que difere entre elas. */
const BucketMovementRow: React.FC<{ movement: BucketMovement }> = ({ movement }) => {
  const display = BUCKET_STATUS_DISPLAY[movement.status];

  return (
    <DataTableRow data-testid={`bucket-movement-row-${movement.id}`} className="bg-neutral-50">
      <DataTableCell />
      <DataTableCell>
        <Text variant="body" size="small" className="text-neutral-900">
          {formatDay(movement.plannedDate)}
        </Text>
        <Text variant="body" size="small" className="text-neutral-500">
          {movement.period != null ? PERIOD_LABELS[movement.period] : 'Sem preferência de turno'}
        </Text>
      </DataTableCell>
      <DataTableCell>
        <Text variant="body" size="small" className="text-neutral-900">
          {BUCKET_TYPE_LABELS[movement.type]}
        </Text>
        {movement.locationType != null && (
          <Text variant="body" size="small" className="text-neutral-500">
            {BUCKET_LOCATION_LABELS[movement.locationType]}
          </Text>
        )}
      </DataTableCell>
      <DataTableCell />
      <DataTableCell />
      <DataTableCell>
        <Text variant="body" size="small" className="text-neutral-500">
          {describeBucketNumbers(movement)}
        </Text>
      </DataTableCell>
      <DataTableCell />
      <DataTableCell>
        <Badge variant={display?.variant ?? 'secondary'} className="whitespace-nowrap">{display?.label ?? '—'}</Badge>
      </DataTableCell>
    </DataTableRow>
  );
};
